const STRIPE_JS_SRC = 'https://js.stripe.com/v3/'

type EmbeddedCheckout = {
  mount: (target: string | HTMLElement) => void
  destroy: () => void
}

type StripePaymentElement = {
  mount: (target: string | HTMLElement) => void
  unmount: () => void
}

type StripeElements = {
  create: (type: 'payment') => StripePaymentElement
}

type ConfirmSetupResult = {
  error?: { message?: string }
  setupIntent?: {
    payment_method?: string | { id?: string } | null
    status?: string
  }
}

type StripeInstance = {
  initEmbeddedCheckout: (options: {
    clientSecret: string
    onComplete?: () => void
  }) => Promise<EmbeddedCheckout>
  elements: (options: {
    clientSecret: string
    appearance?: { theme?: string; variables?: Record<string, string> }
  }) => StripeElements
  confirmSetup: (options: {
    elements: StripeElements
    confirmParams?: { return_url?: string }
    redirect?: 'if_required' | 'always'
  }) => Promise<ConfirmSetupResult>
}

type StripeFactory = (publishableKey: string) => StripeInstance

declare global {
  interface Window {
    Stripe?: StripeFactory
  }
}

const loadStripeScript = (): Promise<StripeFactory> => {
  if (window.Stripe) {
    return Promise.resolve(window.Stripe)
  }

  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${STRIPE_JS_SRC}"]`
    )
    if (existing) {
      existing.addEventListener('load', () => {
        if (window.Stripe) resolve(window.Stripe)
        else reject(new Error('Stripe.js failed to load'))
      })
      existing.addEventListener('error', () =>
        reject(new Error('Stripe.js failed to load'))
      )
      return
    }

    const script = document.createElement('script')
    script.src = STRIPE_JS_SRC
    script.async = true
    script.onload = () => {
      if (window.Stripe) resolve(window.Stripe)
      else reject(new Error('Stripe.js failed to load'))
    }
    script.onerror = () => reject(new Error('Stripe.js failed to load'))
    document.head.appendChild(script)
  })
}

export const mountStripeEmbeddedCheckout = async ({
  publishableKey,
  clientSecret,
  targetId,
  onComplete,
}: {
  publishableKey: string
  clientSecret: string
  targetId: string
  onComplete?: () => void
}): Promise<() => void> => {
  const Stripe = await loadStripeScript()
  const checkout = await Stripe(publishableKey).initEmbeddedCheckout({
    clientSecret,
    onComplete,
  })
  checkout.mount(`#${targetId}`)
  return () => checkout.destroy()
}

const paymentMethodIdFromSetupIntent = (
  setupIntent?: ConfirmSetupResult['setupIntent']
) => {
  const paymentMethod = setupIntent?.payment_method
  if (typeof paymentMethod === 'string' && paymentMethod) {
    return paymentMethod
  }
  if (paymentMethod && typeof paymentMethod === 'object' && paymentMethod.id) {
    return paymentMethod.id
  }
  return null
}

export const mountStripePaymentElement = async ({
  publishableKey,
  clientSecret,
  targetId,
}: {
  publishableKey: string
  clientSecret: string
  targetId: string
}): Promise<{
  confirmSetup: () => Promise<string>
  destroy: () => void
}> => {
  const Stripe = await loadStripeScript()
  const stripe = Stripe(publishableKey)
  const elements = stripe.elements({
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: { colorPrimary: '#0a57a8' },
    },
  })
  const paymentElement = elements.create('payment')
  paymentElement.mount(`#${targetId}`)

  return {
    confirmSetup: async () => {
      const result = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      })
      if (result.error?.message) {
        throw new Error(result.error.message)
      }
      const paymentMethodId = paymentMethodIdFromSetupIntent(result.setupIntent)
      if (!paymentMethodId) {
        throw new Error('Unable to save card. Please try again.')
      }
      return paymentMethodId
    },
    destroy: () => paymentElement.unmount(),
  }
}
